import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import moment from 'moment';
import styles from '../AccountingSystemOpen.module.scss';
import BulkItem from './BulkItem';
import { useClientLogin } from '../../../../../providers/ClientLogin.provider';
import BankingUpdateMiniDialogue from '../BankingUpdateMiniDialogue/BankingUpdateMiniDialogue';
import { PaymentUpdate } from '../types';
import { getAvailablePaymentMethodsDropdown, updateCustomerData, roundCurrency } from '../utils';
import AccountingSystemScheduleSidebar from '../AccountingSystemScheduleDialogue/AccountingSystemScheduleSideBar';
import useFetchRailzBillCreditNotes from '../../../../../hooks/useApi/useFetchRailzBillCreditNotes';
import useFetchRailzInvoiceCreditNotes from '../../../../../hooks/useApi/useFetchRailzInvoiceCreditNotes';
import useDataFetchIndicator from '../../../../../hooks/useDataFetchIndicator';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

interface BankingUpdateDialogueProps {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  listOfSelected: vp.TemporaryBillsOrInvoicesRails[];
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  BusinessName: string;
  ServiceName: string;
  close: (selectedList: vp.TemporaryBillsOrInvoicesRails[], isSaved: boolean) => void;
  onSchedule: (selectedList: vp.TemporaryBillsOrInvoicesRails[]) => void;
  onScheduleDate: (selectedList: vp.TemporaryBillsOrInvoicesRails[], date: string) => void;
  onCollect: (selectedList: vp.TemporaryBillsOrInvoicesRails[]) => void;
}
type FC = FunctionComponent<BankingUpdateDialogueProps>;
const { Modal, SubTitle, Box, DropDown, Icon } = vp;
const BulkPaymentDialogue: FC = ({
  isVisible,
  type,
  close,
  listOfSelected,
  onCollect,
  financialInstitutions,
  onSchedule,
  onScheduleDate,
  BusinessName,
  ServiceName,
}: BankingUpdateDialogueProps) => {
  const [copySelectedList, setCopySelectedList] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [showUpdateBanking, setShowUpdateBanking] = useState(false);
  const [updateType, setUpdateType] = useState<'Edit' | 'Add'>('Edit');
  const [originalValues, setOriginalValues] = useState<vp.TemporaryBillsOrInvoicesRails>();
  const [showCalendar, setShowCalendar] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [bankingDefault, setBankingDefault] = useState<PaymentUpdate>({
    PaymentMethod: '',
    InteracMethod: '',
  });
  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [, , fetchRailzBillsCreditNotes] = useFetchRailzBillCreditNotes({});
  const [, , fetchRailzInvoiceCreditNotes] = useFetchRailzInvoiceCreditNotes({});
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const [appliedCreditNotes, setAppliedCreditNotes] = useState('');
  const [totalCreditsApplied, setTotalCreditsApplied] = useState(0);
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Dialogs.BulkPayment' });
  const { t: tUtils } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Utils' });

  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        setDataFetchingInfo(true, 'loading credit notes', 'fullScreen');
        if (type === 'Receivable') {
          const customerIds = Array.from(new Set(listOfSelected.map((item) => item.customerRef?.id))).filter(
            (item) => item,
          );
          const creditsResponsePromises = customerIds.map((customerId) =>
            fetchRailzInvoiceCreditNotes({
              BusinessName,
              ServiceName,
              CustomerRefID: String(customerId),
            }),
          );
          const creditsResponse = await Promise.all(creditsResponsePromises);

          const aggregatedCreditNotes = creditsResponse
            .filter((response) => response.Success && response.CreditNotes)
            .flatMap((response) => response.CreditNotes);
          setCreditNotes(aggregatedCreditNotes);
        } else {
          const vendorIds = Array.from(new Set(listOfSelected.map((item) => item.vendorRef?.id))).filter(
            (item) => item,
          );
          const creditsResponsePromises = vendorIds.map((vendorId) =>
            fetchRailzBillsCreditNotes({
              BusinessName,
              ServiceName,
              VendorRefID: String(vendorId),
            }),
          );
          const creditsResponse = await Promise.all(creditsResponsePromises);
          const aggregatedCreditNotes = creditsResponse
            .filter((response) => response.Success && response.CreditNotes)
            .flatMap((response) => response.CreditNotes);
          setCreditNotes(aggregatedCreditNotes);
        }
        setDataFetchingInfo(false);
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);

  const {
    response: { Country },
  } = useClientLogin();
  const currency = Country === 'CA' ? 'CAD' : 'USD';
  useEffect(() => {
    const copyOfSelected = [...listOfSelected];

    setCopySelectedList(copyOfSelected);
  }, [listOfSelected, isVisible]);
  useEffect(() => {
    if (isVisible) {
      setIsSaved(false);
    }
  }, [isVisible]);

  useEffect(() => {
    const appliedCreditNotesList = copySelectedList
      .filter((selected) => selected.isSelected)
      .flatMap((selected) => selected.creditNotes?.split(','))
      .filter((selected) => selected);
    setAppliedCreditNotes(appliedCreditNotesList.join(', '));

    const appliedCreditNotesCalc = creditNotes.filter((creditNote) => appliedCreditNotesList.includes(creditNote.id));

    const totalCredits = appliedCreditNotesCalc.reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0);

    setTotalCreditsApplied(totalCredits);
  }, [copySelectedList]);

  const onSaveItem = (item: vp.TemporaryBillsOrInvoicesRails) => {
    const newList: vp.TemporaryBillsOrInvoicesRails[] = copySelectedList.map(
      (selectedListItem: vp.TemporaryBillsOrInvoicesRails) => {
        if (selectedListItem.id === item.id) {
          return item;
        }
        return selectedListItem;
      },
    );

    setCopySelectedList(newList);
  };

  // refactor this function to be used here and bulkItem
  const SaveBank = (values: PaymentUpdate) => {
    if (originalValues) {
      const updatedData = updateCustomerData(values, originalValues, Country, type, originalValues.PaidAmount, '');
      onSaveItem(updatedData);
      setShowUpdateBanking(false);
      setIsSaved(true);
    }
  };

  return (
    <Modal isVisible={isVisible} enableBgClick={false}>
      <div
        className={[styles.dialogueBulk, showUpdateBanking || showCalendar ? styles.disabledOverview : ''].join(' ')}
      >
        <AccountingSystemScheduleSidebar
          isVisible={showCalendar}
          defaultDate={String(moment().add(1, 'day').startOf('day').toDate().getTime())}
          onSchedule={(scheduleDate) => {
            onScheduleDate(
              copySelectedList.filter((item) => item.isSelected === true),
              moment(Number(scheduleDate)).format('YYYY-MM-DD'),
            );
            setShowCalendar(false);
          }}
          close={() => setShowCalendar(false)}
        />
        <BankingUpdateMiniDialogue
          type={type}
          updateType={updateType}
          isVisible={showUpdateBanking}
          financialInstitutions={financialInstitutions}
          originalValue={originalValues}
          defaultValues={bankingDefault}
          close={() => setShowUpdateBanking(false)}
          onSave={(values) => {
            SaveBank(values);
          }}
        />

        <Icon
          className={styles.closeButton}
          icon="cross"
          height="12px"
          width="12px"
          onClick={() => {
            close(copySelectedList, isSaved);
          }}
        />

        <Box pb="lg" base="8" flx="col">
          <SubTitle center subTitle={`${type === 'Receivable' ? t('collectTitle') : t('payTitle')} ${type}`} />
          <Box gap="lg" pt="md" base="8" flx="col">
            <Box jc="center" height="auto">
              {type === 'Receivable' && (
                <div className={styles.dialogueParagraph} style={{ textAlign: 'center' }}>
                  {t('receivableDescription')}
                </div>
              )}
              {type === 'Payable' && (
                <div className={styles.dialogueParagraph} style={{ textAlign: 'center' }}>
                  {t('payableDescription')}
                </div>
              )}
            </Box>

            <Box>
              <div className={styles.bankList}>
                {copySelectedList.map((selectedItem) => (
                  <BulkItem
                    paymentMethods={getAvailablePaymentMethodsDropdown(
                      selectedItem,
                      Country,
                      type,
                      (newPaymentMethod) => {
                        setUpdateType('Edit');
                        setOriginalValues(selectedItem);
                        const currentValues: PaymentUpdate = {
                          PaymentMethod: newPaymentMethod,
                          FinancialInstitutionNumber: selectedItem?.Customer?.BankAccount?.FinancialInstitutionNumber,
                          BankTransitNumber: selectedItem?.Customer?.BankAccount?.BranchTransitNumber,
                          AccountNumber: selectedItem?.Customer?.BankAccount?.AccountNumber,
                          Email: selectedItem?.Customer?.Email || selectedItem?.Customer?.Interac?.EmailAddress,
                          Phone: selectedItem?.Customer?.Phone || selectedItem?.Customer?.Interac?.PhoneNumber || '',
                          Question: selectedItem?.Customer?.Interac?.Question,
                          Answer: selectedItem?.Customer?.Interac?.Answer,
                          PhoneNumber: selectedItem?.Customer?.BankAccount?.PhoneNumber,
                          City: selectedItem?.Customer?.BankAccount?.City,
                          Address: selectedItem?.Customer?.BankAccount?.Address,
                          State: selectedItem?.Customer?.BankAccount?.State,
                          ZipCode: selectedItem?.Customer?.BankAccount?.ZipCode,
                          InteracMethod: selectedItem?.Customer?.Interac?.EmailAddress
                            ? 'Email Address'
                            : selectedItem?.Customer?.Interac?.PhoneNumber
                              ? 'Phone Number'
                              : 'Email Address',
                          TokenDebit: selectedItem?.Customer?.DebitCard?.Token,
                          TokenCredit: selectedItem?.Customer?.CreditCard?.Token,
                          Language: selectedItem?.Customer?.Interac?.Language || 'en',
                        };
                        setBankingDefault(currentValues);
                        setShowUpdateBanking(true);
                      },
                      tUtils,
                    )}
                    defaultValues={selectedItem}
                    creditNotes={creditNotes.filter(
                      (creditNote) =>
                        (creditNote.customerRef?.id && creditNote.customerRef.id === selectedItem.customerRef?.id) ||
                        (creditNote.vendorRef?.id && creditNote.vendorRef.id === selectedItem.vendorRef?.id),
                    )}
                    appliedCreditNotes={appliedCreditNotes.split(',')}
                    id={selectedItem.id}
                    onSave={onSaveItem}
                    onUpdate={(innerUpdateType, innerOriginalValues, innerBankingDefault) => {
                      setUpdateType(innerUpdateType);
                      setOriginalValues(innerOriginalValues);
                      setBankingDefault(innerBankingDefault);
                      setShowUpdateBanking(true);
                    }}
                    type={type}
                  />
                ))}
              </div>
            </Box>
            <div className={styles.greyCard}>
              <div className={styles.cardBulkTotalContainer}>
                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>{t('totalSelected')}</div>
                  <div className={styles.cardBulkValue}>
                    {currency}{' '}
                    {vp.formattedCurrency(
                      String(
                        copySelectedList
                          .filter((item) => item.isSelected)
                          .reduce(
                            (remainingAmount, item) =>
                              remainingAmount +
                              Number(vp.sanitizeToNumeric(String(item.amountDue ? item.amountDue : item.totalAmount))),
                            0,
                          ),
                      ),
                    )}
                  </div>
                </div>
                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>
                    {type === 'Receivable' ? t('collectViaPaymentMethod') : t('payViaPaymentMethod')}
                  </div>
                  <div className={styles.cardBulkValue}>
                    {currency}{' '}
                    {vp.formattedCurrency(
                      String(
                        copySelectedList
                          .filter((item) => item.isSelected)
                          .reduce((remainingAmount, item) => remainingAmount + Number(item.PaidAmount), 0),
                      ),
                    )}
                  </div>
                </div>
                <div className={styles.cardBulkTotalItems}>
                  <div className={styles.cardBulkLabel}>{t('creditsApplied')}</div>
                  <div className={styles.cardBulkValue}>
                    {currency} {vp.formattedCurrency(String(totalCreditsApplied))}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.dialogueInfo}>{t('addMissingPaymentMethodInfo')}</div>
          </Box>
        </Box>
        <Box jc="sb" gap="sm" flx="row">
          <Box flx="col" jc="fe">
            <Box flx="row" jc="fs">
              <Button
                id="export-transactions"
                colorScheme="secondary"
                size="standard"
                iconRight="keyboard_arrow_down"
                onClick={() => {
                  setIsOptionVisible(true);
                }}
                disabled={
                  copySelectedList.some(
                    (item) =>
                      item.hasBankAccount === false &&
                      item.hasCreditCard === false &&
                      item.hasDebitCard === false &&
                      item.hasInterac === false &&
                      item.paymentMethodUser !== 'eLinx' &&
                      item.isSelected === true,
                  ) ||
                  copySelectedList.filter((item) => item.isSelected === true).length === 0 ||
                  copySelectedList.some((item) => item.isSelected === true && item.paymentMethodUser === 'eLinx')
                }
              >
                <Box pe="sm" ai="center">
                  {type === 'Receivable' ? t('ScheduleDropdown.receivableLabel') : t('ScheduleDropdown.payableLabel')}
                </Box>
              </Button>
              <div />
            </Box>
            <div style={{ width: '215px' }} />
            <DropDown isMenuVisible={isOptionVisible} onClick={setIsOptionVisible} header={false}>
              <vp.Button
                id="collect-on-due-date-button"
                label={
                  type === 'Receivable' ? t('ScheduleDropdown.collectOnDueDate') : t('ScheduleDropdown.payOnDueDate')
                }
                color="none"
                background="transparent"
                textSize={14}
                fontUnit="px"
                width="100%"
                noShadow
                textAlign="left"
                iconLocation="left"
                height="38px"
                icon="bill"
                iconWidth="27px"
                iconHeight="24px"
                justifyLabel="fs"
                onClick={() => {
                  setIsOptionVisible(false);
                  onSchedule(copySelectedList.filter((item) => item.isSelected === true));
                }}
              />
              <vp.Button
                id="custom-date-button"
                label={t('ScheduleDropdown.customDate')}
                color="none"
                background="transparent"
                textSize={14}
                fontUnit="px"
                width="100%"
                noShadow
                textAlign="left"
                iconLocation="left"
                icon="calendarSchedule"
                height="38px"
                iconWidth="27px"
                iconHeight="24px"
                justifyLabel="fs"
                onClick={() => {
                  setIsOptionVisible(false);
                  setShowCalendar(true);
                }}
              />
            </DropDown>
          </Box>
          <Button
            id="save-and-collect"
            onClick={() => {
              onCollect(copySelectedList.filter((item) => item.isSelected === true));
            }}
            colorScheme="primary"
            size="standard"
            disabled={
              copySelectedList.some(
                (item) =>
                  item.hasBankAccount === false &&
                  item.hasCreditCard === false &&
                  item.hasDebitCard === false &&
                  item.hasInterac === false &&
                  item.paymentMethodUser !== 'eLinx' &&
                  item.isSelected === true,
              ) || copySelectedList.filter((item) => item.isSelected === true).length === 0
            }
          >
            {type === 'Receivable' ? t('saveAndCollect') : t('saveAndPay')}
          </Button>
        </Box>
        <Box pt="sm" flx="col">
          <div className={styles.disabledMessage}>
            {t('footerMessage', { type: type === 'Receivable' ? 'Collect' : 'Pay' })}
          </div>
        </Box>
      </div>
    </Modal>
  );
};

export default BulkPaymentDialogue;
